import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import Input from "../common/Input";
import { invoiceService } from "../../services/proposals";
import { clientService } from "../../services/clients";
import toast from "react-hot-toast";

const InvoiceForm = ({ invoice, onSuccess }) => {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset } = useForm({
    defaultValues: invoice
      ? {
          invoiceNumber: invoice.invoiceNumber,
          clientId: invoice.client?._id || invoice.client,
          dueDate: invoice.dueDate?.slice(0, 10),
          gstRate: invoice.gst && invoice.subtotal ? ((invoice.gst / invoice.subtotal) * 100).toFixed(0) : 0,
          status: invoice.status || "pending",
          paidAmount: invoice.paidAmount || 0,
        }
      : {},
  });
  const [items, setItems] = useState(
    invoice?.items || []
  );
  const [loading, setLoading] = useState(false);

  const { data: clients } = useQuery({
    queryKey: ["clients"],
    queryFn: clientService.getAll,
  });

  useEffect(() => {
    if (invoice) {
      reset({
        invoiceNumber: invoice.invoiceNumber,
        clientId: invoice.client?._id || invoice.client,
        dueDate: invoice.dueDate?.slice(0, 10),
        gstRate: invoice.gst && invoice.subtotal ? ((invoice.gst / invoice.subtotal) * 100).toFixed(0) : 0,
        status: invoice.status || "pending",
        paidAmount: invoice.paidAmount || 0,
      });
      setItems(invoice.items || []);
    } else {
      reset({});
      setItems([]);
    }
  }, [invoice, reset]);

  const addItem = () => {
    setItems([...items, { description: "", quantity: 1, rate: 0, amount: 0 }]);
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    updated[index].amount = updated[index].quantity * updated[index].rate;
    setItems(updated);
  };

  const onSubmit = async (data) => {
    const subtotal = items.reduce((sum, i) => sum + (i.amount || 0), 0);
    const gst = subtotal * (data.gstRate / 100);
    const total = subtotal + gst;
    const invoiceData = {
      invoiceNumber: data.invoiceNumber,
      client: data.clientId,
      items,
      subtotal,
      gst,
      total,
      dueDate: data.dueDate,
      status: data.status,
      paidAmount: parseFloat(data.paidAmount) || 0,
    };
    setLoading(true);
    try {
      if (invoice) {
        await invoiceService.update(invoice._id, invoiceData);
        toast.success("Invoice updated");
      } else {
        await invoiceService.create(invoiceData);
        toast.success("Invoice created");
      }
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      reset();
      setItems([]);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Invoice Number"
        {...register("invoiceNumber", { required: true })}
      />
      <div>
        <label className="block text-sm font-medium mb-1">Client</label>
        <select
          {...register("clientId", { required: true })}
          className="w-full border rounded p-2"
        >
          <option value="">Select Client</option>
          {clients?.map((client) => (
            <option key={client._id} value={client._id}>
              {client.name}
            </option>
          ))}
        </select>
      </div>
      <Input
        label="Due Date"
        type="date"
        {...register("dueDate", { required: true })}
      />
      <Input
        label="GST Rate (%)"
        type="number"
        {...register("gstRate", { required: true })}
      />
      <div>
        <label className="block text-sm font-medium mb-1">Status</label>
        <select
          {...register("status")}
          className="w-full border rounded p-2"
        >
          <option value="pending">Pending</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </select>
      </div>
      <Input
        label="Paid Amount"
        type="number"
        {...register("paidAmount")}
      />
      <div>
        <h3 className="text-lg font-medium">Items</h3>
        {items.map((item, idx) => (
          <div key={idx} className="flex space-x-2 mb-2">
            <Input
              placeholder="Description"
              value={item.description}
              onChange={(e) => updateItem(idx, "description", e.target.value)}
            />
            <Input
              type="number"
              placeholder="Qty"
              value={item.quantity}
              onChange={(e) => updateItem(idx, "quantity", e.target.value)}
            />
            <Input
              type="number"
              placeholder="Rate"
              value={item.rate}
              onChange={(e) => updateItem(idx, "rate", e.target.value)}
            />
            <span className="py-2">Amount: ₹{item.amount}</span>
          </div>
        ))}
        <Button type="button" variant="secondary" onClick={addItem}>
          Add Item
        </Button>
      </div>
      <Button type="submit" disabled={loading}>
        {loading ? "Saving..." : invoice ? "Update Invoice" : "Create Invoice"}
      </Button>
    </form>
  );
};

export default InvoiceForm;
